import { useMemo, useState } from "react";

import { CloseIcon, DownloadIcon } from "@/assets/icons";
import { Button, Modal } from "@/atoms";
import { strings } from "@/constants";
import {
    AlertVariantEnum,
    ButtonSizeEnum,
    ButtonVariantEnum,
    JobStatusEnum,
} from "@/enums";
import { useToast } from "@/hooks";
import type { BenchmarkListItemType } from "@/types";
import { downloadBenchmarkXlsxHandler, formatDateHandler, renderBenchmarkXlsxHtmlHandler } from "@/utils";

const cardClass = `
    overflow-hidden
    rounded-default
    border
    border-gray-200
    bg-surface
    shadow-sm
`;

const placeholderClass = `
    px-6
    py-12
    text-center
    text-sm
    text-text-secondary
`;

const statusBadgeStyleHandler = (status?: string): string => {
    const value = (status ?? "").toLowerCase();

    if (value === JobStatusEnum.COMPLETED) return "text-primary bg-primary/10";

    if (value === JobStatusEnum.FAILED) return "text-error bg-error/10";

    if (value === JobStatusEnum.PROCESSING) return "text-yellow-700 bg-yellow-100";

    return "text-text-secondary bg-gray-100";
};

const statusLabelHandler = (status?: string): string => {
    const value = (status ?? "").toLowerCase();

    if (value === JobStatusEnum.COMPLETED) return strings.benchmarks.statusCompleted;

    if (value === JobStatusEnum.FAILED) return strings.benchmarks.statusFailed;

    if (value === JobStatusEnum.PROCESSING) return strings.benchmarks.statusProcessing;

    return strings.benchmarks.statusPending;
};

const buildFileNameHandler = (jobTitle?: string, jobId?: string): string => {
    const slugSource = jobTitle?.trim() || jobId || "result";

    const slug = slugSource.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") || "result";

    return `${strings.result.xlsxFileNamePrefix}-${slug}`;
};

const hasResultDataHandler = (item: BenchmarkListItemType): boolean => {
    const payload = item.resultPayload;

    if (!payload) return false;

    if ((payload.selectedBranches?.length ?? 0) > 0) return true;

    if ((payload.environmentStatus?.length ?? 0) > 0) return true;

    return false;
};

export const BenchmarksList = ({
    isLoading = false,
    items,
}: {
    isLoading?: boolean,
    items: BenchmarkListItemType[],
}) => {
    const [downloadingId, setDownloadingId] = useState<number | string | null>(null);

    const [viewItem, setViewItem] = useState<BenchmarkListItemType | null>(null);

    const { onShow: showToastHandler } = useToast();

    const viewHtml = useMemo(
        () => {
            if (!viewItem?.resultPayload) return "";

            return renderBenchmarkXlsxHtmlHandler({
                industry: viewItem.industry,
                jobTitle: viewItem.jobTitle,
                result: viewItem.resultPayload,
                seniority: viewItem.seniority,
            });
        },
        [viewItem],
    );

    const viewHandler = (item: BenchmarkListItemType) => {
        if (!hasResultDataHandler(item)) {
            showToastHandler({
                message: strings.benchmarks.downloadNotReady,
                variant: AlertVariantEnum.WARNING,
            });

            return;
        }

        setViewItem(item);
    };

    const closeViewHandler = () => {
        setViewItem(null);
    };

    const downloadHandler = async (item: BenchmarkListItemType) => {
        if (!hasResultDataHandler(item) || !item.resultPayload) {
            showToastHandler({
                message: strings.benchmarks.downloadNotReady,
                variant: AlertVariantEnum.WARNING,
            });

            return;
        }

        setDownloadingId(item.id);

        try {
            downloadBenchmarkXlsxHandler(
                {
                    industry: item.industry,
                    jobTitle: item.jobTitle,
                    result: item.resultPayload,
                    seniority: item.seniority,
                },
                buildFileNameHandler(
                    item.jobTitle,
                    String(item.jobId),
                ),
            );
        } catch (error) {
            showToastHandler({
                message: error instanceof Error ? error.message : strings.benchmarks.downloadFailed,
                variant: AlertVariantEnum.ERROR,
            });
        } finally {
            setDownloadingId(null);
        }
    };

    return (
        <>
            <div className={cardClass}>
                {isLoading
                    ? <div className={placeholderClass}>{strings.benchmarks.loading}</div>
                    : items.length === 0
                        ? <div className={placeholderClass}>{strings.benchmarks.empty}</div>
                        : (
                            <ul className="divide-y divide-gray-100">
                                {items.map((item) => {
                                    const isDownloading = downloadingId === item.id;

                                    const canDownload = item.status === JobStatusEnum.COMPLETED && hasResultDataHandler(item);

                                    return (
                                        <li
                                            key={String(item.id)}
                                            className="
                                                flex
                                                flex-col
                                                gap-3
                                                px-4
                                                py-4
                                                transition-colors
                                                hover:bg-gray-50
                                                sm:flex-row
                                                sm:items-center
                                                sm:justify-between
                                                sm:px-6
                                            "
                                        >
                                            <div className="min-w-0 flex-1 space-y-1">
                                                <div
                                                    className="
                                                        flex
                                                        flex-wrap
                                                        items-center
                                                        gap-2
                                                    "
                                                >
                                                    <span
                                                        className="
                                                            font-mono
                                                            text-sm
                                                            font-semibold
                                                            text-text-primary
                                                        "
                                                    >
                                                        {item.jobTitle ?? `#${item.id}`}
                                                    </span>
                                                    <span
                                                        className={`
                                                            shrink-0
                                                            rounded-full
                                                            px-2
                                                            py-0.5
                                                            text-xs
                                                            font-semibold
                                                            tracking-wide
                                                            uppercase
                                                            ${statusBadgeStyleHandler(item.status)}
                                                        `}
                                                    >
                                                        {statusLabelHandler(item.status)}
                                                    </span>
                                                </div>
                                                {(item.completedAt || item.createdAt) && (
                                                    <p className="text-xs text-text-secondary">
                                                        {strings.benchmarks.completed}
                                                        {": "}
                                                        {formatDateHandler(item.completedAt ?? item.createdAt ?? "")}
                                                    </p>
                                                )}
                                            </div>
                                            <div
                                                className="
                                                    flex
                                                    shrink-0
                                                    items-center
                                                    gap-2
                                                "
                                            >
                                                <Button
                                                    disabled={!canDownload}
                                                    size={ButtonSizeEnum.SM}
                                                    variant={ButtonVariantEnum.LINK}
                                                    onClick={() => viewHandler(item)}
                                                >
                                                    {strings.benchmarks.view}
                                                </Button>
                                                <Button
                                                    className="
                                                        inline-flex
                                                        items-center
                                                        gap-1.5
                                                    "
                                                    disabled={!canDownload}
                                                    isLoading={isDownloading}
                                                    size={ButtonSizeEnum.SM}
                                                    onClick={() => downloadHandler(item)}
                                                >
                                                    <DownloadIcon className="size-3.5" />
                                                    {strings.benchmarks.download}
                                                </Button>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
            </div>
            <Modal
                className="
                    flex
                    h-[85vh]
                    max-w-3xl
                    flex-col
                    overflow-hidden
                "
                isOpen={!!viewItem}
                onClose={closeViewHandler}
            >
                <div
                    className="
                        flex
                        items-center
                        justify-between
                        gap-3
                        border-b
                        border-gray-100
                        px-5
                        py-4
                    "
                >
                    <div className="min-w-0">
                        <p
                            className="
                                text-xs
                                tracking-wide
                                text-text-secondary
                                uppercase
                            "
                        >
                            {strings.benchmarks.title}
                        </p>
                        <p
                            className="
                                truncate
                                font-mono
                                text-sm
                                font-semibold
                                text-text-primary
                            "
                        >
                            {viewItem?.jobTitle ?? ""}
                        </p>
                    </div>
                    <div
                        className="
                            flex
                            shrink-0
                            items-center
                            gap-2
                        "
                    >
                        <Button
                            className="
                                inline-flex
                                items-center
                                gap-1.5
                            "
                            disabled={!viewItem || !hasResultDataHandler(viewItem)}
                            isLoading={downloadingId === viewItem?.id}
                            size={ButtonSizeEnum.SM}
                            onClick={() => viewItem && downloadHandler(viewItem)}
                        >
                            <DownloadIcon className="size-3.5" />
                            {strings.benchmarks.download}
                        </Button>
                        <Button
                            aria-label={strings.common.closeAlert}
                            className="
                                inline-flex
                                items-center
                                justify-center
                                p-1
                            "
                            size={ButtonSizeEnum.SM}
                            variant={ButtonVariantEnum.GHOST}
                            onClick={closeViewHandler}
                        >
                            <CloseIcon className="size-5" />
                        </Button>
                    </div>
                </div>
                <div
                    className="
                        min-h-0
                        flex-1
                        overflow-auto
                        p-3
                        sm:p-4
                    "
                >
                    {viewItem && (
                        <div
                            className="
                                benchmark-xlsx-preview
                                text-sm
                                text-text-primary
                                [&_table]:w-full
                                [&_table]:border-collapse
                                [&_td]:border
                                [&_td]:border-gray-200
                                [&_td]:px-2
                                [&_td]:py-1.5
                                [&_th]:border
                                [&_th]:border-gray-200
                                [&_th]:bg-primary/5
                                [&_th]:px-2
                                [&_th]:py-1.5
                                [&_th]:text-left
                                [&_th]:font-semibold
                            "
                            dangerouslySetInnerHTML={{ __html: viewHtml }}
                        />
                    )}
                </div>
            </Modal>
        </>
    );
};
